import React, { FC, PointerEvent, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PanelHeading from "./PanelHeading";
import NoticeBar from "./NoticeBar";
import LegalCheck from "./LegalCheck";
import StepFooter from "./StepFooter";
import { useReservation } from "./reservationContext";
import { useLocale } from "../i18n/locale";
import { formatSignatureDate } from "../i18n/formatDate";

export interface SignatureCaptureProps {
  /** Named in the subtitle: what this signature will be applied to. */
  documentLabel: string;
  /** True when more than one document is covered, for the plural in the consent. */
  plural: boolean;
  backLabel: string;
  onBack: () => void;
  /** Receives the drawn signature as a PNG data URL. */
  onConfirm: (signature: string) => void;
  /** True while the signature is being saved; holds the confirm button. */
  submitting?: boolean;
  /** Set when the save failed, so the tenant is told to try again. */
  failed?: boolean;
}

/** The canvas is fixed-resolution and scaled by CSS, so strokes stay crisp. */
const CANVAS_WIDTH = 1000;
const CANVAS_HEIGHT = 310;

/**
 * The full-column signature screen. It replaces the centre panel rather than
 * opening a dialog — signing is the task at that moment, and the reference
 * gives it the whole column.
 *
 * Pointer events cover mouse, pen and touch in one path; `touch-none` stops the
 * browser scrolling the page while a finger is drawing.
 */
const SignatureCapture: FC<SignatureCaptureProps> = ({
  documentLabel,
  plural,
  backLabel,
  onBack,
  onConfirm,
  submitting = false,
  failed = false,
}) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [consented, setConsented] = useState(false);
  const c = t.desktop.signaturePanel;

  // The signature is dated the day it is drawn, not the day of the stay.
  const signedOn = formatSignatureDate(locale);

  /** Canvas coordinates for a pointer, corrected for the CSS scale. */
  const pointFor = (e: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const box = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - box.left) * (canvas.width / box.width),
      y: (e.clientY - box.top) * (canvas.height / box.height),
    };
  };

  const handlePointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    // Capture keeps the stroke alive if the pointer leaves the canvas mid-flick.
    canvas.setPointerCapture(e.pointerId);
    isDrawing.current = true;

    const { x, y } = pointFor(e);
    const ctx = canvas.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handlePointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current) return;

    const { x, y } = pointFor(e);
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#13263f";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  return (
    <div>
      <PanelHeading
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitlePrefix}
            <strong className="font-bold text-navy">{documentLabel}</strong>.
          </>
        }
      />

      <NoticeBar
        className="mt-6"
        icon={<span className="text-[20px] leading-none">✎</span>}
        title={c.instructionTitle}
        body={c.instructionBody}
      />

      <div className="relative mt-4 h-[220px] overflow-hidden border-[1.5px] border-dashed border-[#bcb5aa] bg-[#fffdf9] min-[760px]:h-[255px]">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onPointerLeave={stopDrawing}
          aria-label={c.placeholder}
          className="relative z-[2] block h-full w-full cursor-crosshair touch-none"
        />

        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center font-serif text-[31px] italic text-[#d4cdc2]">
            {c.placeholder}
          </div>
        )}

        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          // Signing is the one screen a tenant is guaranteed to use with a
          // finger, so Clear gets a full 44px target on a phone.
          className="absolute right-[13px] top-[13px] z-[3] min-h-[44px] bg-[#f2eee7] px-3 py-[7px] text-[11px] text-[#806d56] transition-opacity hover:bg-[#e9e4da] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-45 min-[760px]:min-h-0 min-[760px]:px-2.5 min-[760px]:text-[10px]"
        >
          {c.clear}
        </button>

        <div
          className="pointer-events-none absolute bottom-[54px] left-[8%] right-[8%] border-b border-[#cfc6b9]"
          aria-hidden="true"
        />
      </div>

      <div className="grid grid-cols-1 border border-t-0 border-line bg-[#faf9f6] min-[760px]:grid-cols-2">
        <div className="px-4 py-[14px]">
          <small className="block text-[12px] min-[760px]:text-[12px] uppercase tracking-[0.12em] text-[#9b948a]">
            {c.tenantName}
          </small>
          <strong className="mt-[5px] block text-[13px] font-bold text-navy">
            {reservation.tenantName}
          </strong>
        </div>
        <div className="border-t border-line px-4 py-[14px] min-[760px]:border-l min-[760px]:border-t-0">
          <small className="block text-[12px] min-[760px]:text-[12px] uppercase tracking-[0.12em] text-[#9b948a]">
            {c.dateOfSignature}
          </small>
          <strong className="mt-[5px] block text-[13px] font-bold text-navy">
            {signedOn}
          </strong>
        </div>
      </div>

      <LegalCheck
        id="signature-consent"
        checked={consented}
        onChange={setConsented}
        className="mx-0.5 mb-[18px] mt-3.5"
      >
        {c.consent(plural)}
      </LegalCheck>

      {failed && (
        <p
          role="alert"
          className="mb-3 border border-[#d8a49c] bg-[#fbf0ee] p-[11px_13px] text-[11px] text-[#8c3a30]"
        >
          {c.saveFailed}
        </p>
      )}

      <StepFooter
        onBack={onBack}
        backLabel={backLabel}
        onContinue={() => {
          if (!hasInk || !consented || submitting) return;
          onConfirm(canvasRef.current!.toDataURL("image/png"));
        }}
        label={submitting ? c.confirming : c.confirm}
        // Saving is reported separately from the gate: both stop the click, but
        // a signature being saved is progress, not a refusal.
        disabled={!hasInk || !consented}
        busy={submitting}
      />

      {/* Saving the signature can take a while (the server countersigns and
          regenerates the PDF). A full-page overlay makes the wait obvious and
          blocks a second submit. Portaled to <body> so it covers the whole
          viewport, not just this column. */}
      {submitting &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center"
            role="alert"
            aria-busy="true"
          >
            <div className="flex flex-col items-center gap-3 rounded-[10px] bg-[#f3eee6] px-8 py-6 shadow-[0_18px_44px_rgba(20,38,63,0.18)]">
              <div className="h-11 w-11 animate-spin rounded-full border-[3px] border-[#e5e0d7] border-t-navy" />
              <p className="text-[14px] font-medium text-navy">{c.confirming}</p>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SignatureCapture;
